import {
  PriceForm,
  Product,
  ProductFormDetailsInterface,
} from 'types/productTypes';
import { StrToTwoDecimal } from 'utils/maths/Str2Num';
import {
  transformProductAPIToProductOptionsSectionForm,
  transformProductOptionOfItemFormToAPIFormat,
} from 'utils/productOption/converter';

export const extractTagsFromProductFormForAPI = (tags: string[] = []) => {
  const newTags: string[] = [];
  const existingTagIds: number[] = [];

  if (!Array.isArray(tags)) return { newTags, existingTagIds };

  for (const tag of tags) {
    if (typeof tag === 'string' && tag.startsWith('new:')) {
      newTags.push(tag.replace('new:', ''));
    } else {
      existingTagIds.push(parseInt(tag, 10));
    }
  }

  return { newTags, existingTagIds };
};

export const transformProductForm = (
  {
    productName,
    productCode,
    barcode,
    category,
    supplier,
    productDetails,
    cost_price,
    unit_price,
    unit_price1,
    unit_price2,
    unit_price3,
    unit_price4,
    vat,
    product_stock_type,
    inventory_type,
    unit_type,
    first_page_pos,
    display_pos,
    composites,
    quantity_notify,
    productOptions,
    tags,
  }: ProductFormDetailsInterface,
  imgName: string,
  MerchantID: number,
  isEdit = false,
  isDeleteImg = 0
): Product => {
  // TODO: Retrieve store_id from session or relevant source in the future
  const storeId = MerchantID;
  const form: Product = {
    store_id: storeId,
    code: productCode,
    name: productName,
    barcode: barcode,
    category_number: parseInt(category, 10),
    supplier_id: parseInt(supplier, 10),
    image: imgName && imgName !== '' ? imgName : '',

    description: productDetails,
    first_page_pos: first_page_pos,
    display_pos: display_pos,
    cost_price: cost_price
      ? {
          value: StrToTwoDecimal(cost_price.value),
          apply_all: cost_price.apply_all,
        }
      : { value: 0, apply_all: 0 },
    unit_price: unit_price
      ? {
          value: StrToTwoDecimal(unit_price.value),
          apply_all: unit_price.apply_all,
        }
      : { value: 0, apply_all: 0 },
    unit_price1: unit_price1
      ? {
          value: StrToTwoDecimal(unit_price1.value),
          apply_all: unit_price1.apply_all,
        }
      : { value: 0, apply_all: 0 },
    unit_price2: unit_price2
      ? {
          value: StrToTwoDecimal(unit_price2.value),
          apply_all: unit_price2.apply_all,
        }
      : { value: 0, apply_all: 0 },
    unit_price3: unit_price3
      ? {
          value: StrToTwoDecimal(unit_price3.value),
          apply_all: unit_price3.apply_all,
        }
      : { value: 0, apply_all: 0 },
    unit_price4: unit_price4
      ? {
          value: StrToTwoDecimal(unit_price4.value),
          apply_all: unit_price4.apply_all,
        }
      : { value: 0, apply_all: 0 },
    vat: StrToTwoDecimal(vat),
    product_stock_type: parseInt(product_stock_type, 10),
    unit_type: product_stock_type === '1' ? 0 : parseInt(unit_type, 10),
    inventory_type:
      product_stock_type === '1' ? 3 : parseInt(inventory_type, 10),
    composites:
      inventory_type !== '2' || product_stock_type === '1' ? [] : composites,
    quantity_notify: StrToTwoDecimal(quantity_notify),
    product_options:
      transformProductOptionOfItemFormToAPIFormat(productOptions),
  };
  if (isEdit) {
    form.delete_image = isDeleteImg;
  }
  if (tags && tags.length > 0) {
    const { newTags, existingTagIds } = extractTagsFromProductFormForAPI(tags);
    form.tags = {
      tags: existingTagIds,
      new_tags: newTags,
    };
  }
  return form;
};

export const transformProductDataFromAPI = async (
  data: any
): Promise<ProductFormDetailsInterface> => {
  const formatPrice = (price: number): PriceForm => {
    return {
      value: price.toFixed(2),
      apply_all: 0,
    };
  };

  return {
    productName: data.name,
    productCode: data.code,
    pageConfig: {
      custom_item_code: true,
      default_tax_1_rate: 0,
      group_price: [],
    },
    barcode: data.barcode,
    category: data.category_number.toString(),
    supplier: data.supplier_id.toString(),
    productDetails: data.description,
    cost_price: formatPrice(data.cost_price),
    unit_price: formatPrice(data.unit_price),
    unit_price1: formatPrice(data.unit_price1),
    unit_price2: formatPrice(data.unit_price2),
    unit_price3: formatPrice(data.unit_price3),
    unit_price4: formatPrice(data.unit_price4),
    vat: data.vat.toString(),
    product_stock_type: data.product_stock_type.toString(),
    unit_type: data.unit_type.toString(),
    inventory_type:
      data.product_stock_type === 1 ? '3' : data.inventory_type.toString(),
    first_page_pos: data.first_page_pos,
    display_pos: data.display_pos,
    productOptions: data.product_options
      ? transformProductAPIToProductOptionsSectionForm(data.product_options)
      : [],
    pricesByBranch: data.prices_by_branch || [],
    composites: data.composites.map((composite: any) => ({
      name: composite.name,
      item_id_number: composite.item_id_number,
      quantity: composite.quantity,
    })),
    quantity_notify: data.quantity_notify.toString(),
    tags: data.tags
      ? data.tags.map((tag: { id: number; name: string }) => tag.id.toString())
      : [],
  };
};

export const transformProductData2Options = async (products: any) => {
  return [
    {
      options: products.map(
        (product: {
          name: any;
          // item_id_number: any;
          code: any;
          images: { thumbs: any };
        }) => ({
          label: product.name,
          // value: product.item_id_number,
          value: product.code,
          image: product.images.thumbs,
        })
      ),
    },
  ];
};

export const normalizePrices = (data: Record<string, any>) => {
  const keysToConvert = [
    'cost_price',
    'unit_price',
    'unit_price1',
    'unit_price2',
    'unit_price3',
    'unit_price4',
  ];

  const result = { ...data };
  keysToConvert.forEach((key) => {
    if (result[key] !== undefined) {
      const cleaned = String(result[key]).replace(/,/g, '');
      result[key] = parseFloat(cleaned) || 0;
    }
  });

  return result;
};
